import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Lookup,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import type { RowPreparedEvent } from 'devextreme/ui/data_grid';
import SelectBox from 'devextreme-react/select-box';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ActiveUsersBar } from '../realtime/ActiveUsersBar';
import { RecordEditDiscussionPopup, OPS_SITE_GRID_PROPS, OpsGridStateStoring, opsGridFormPopupSizeKey } from '../opsDataGrid';
import { CoveredEntityDetailView } from './CoveredEntityDetailView';
import { usePatientTrackingCsvImport } from './PatientTrackingCsvImportButton';
import { PatientTrackingGridToolbarItems } from './PatientTrackingGridToolbar';
import { patientTrackingApi } from './patientTrackingApi';
import { useLiveRows } from './useLiveRows';
import {
  CE_STATUS_OPTIONS,
  PatientTrackingBoardKeys,
  type CeStatus,
  type CoveredEntityDto,
  type CoveredEntityUpsertBody,
  type OurPharmacyDto,
  type PatientTrackingCsvImportConfig
} from './patientTrackingTypes';
import { EntraGroupDisplayNameField, type EntraGroupSearchResult } from './EntraGroupDisplayNameField';
import './patientTracking.css';

const COVERED_ENTITY_GRID_SCHEMA_VERSION = 1;
const COVERED_ENTITY_GRID_COLUMNS = [
  'ceStatus',
  'pharmacyName',
  'coveredEntityName',
  'ceCodes',
  'portalGroupName',
  'sendAlertsToEmail',
  'ccAlertsToEmail',
  'ceOptOut',
  'totalRecords',
  'pendingCount',
  'reviewedCount',
  'sentCount',
  'followUpCount',
  'onHoldCount',
  'resolvedCount',
  'lostCount',
  'totalAlertsSent',
  'lastAlertSent'
] as const;

export interface CoveredEntityGridProps {
  currentUserId: string;
  currentUserName?: string;
  currentUserAliases?: Iterable<string>;
  /** Called after a CE write so dependent views (patient CE lookups) can refresh. */
  onChanged?: () => void;
  csvImport?: PatientTrackingCsvImportConfig;
  /** Graph group search for portal alert group (client tenant). Falls back to plain text when omitted. */
  searchPortalAlertGroups?: (query: string) => Promise<EntraGroupSearchResult[]>;
  stateStorageKey?: string;
}

const emptyForm = (): CoveredEntityUpsertBody => ({
  pharmacyId: '',
  ceStatus: 'Pending',
  coveredEntityName: '',
  ceCodes: null,
  portalGroupName: null,
  sendAlertsToEmail: null,
  ccAlertsToEmail: null,
  ceOptOut: false,
  ceGeneralNote: null
});

function toBody(c: CoveredEntityDto): CoveredEntityUpsertBody {
  return {
    pharmacyId: c.pharmacyId,
    ceStatus: c.ceStatus,
    coveredEntityName: c.coveredEntityName,
    ceCodes: c.ceCodes,
    portalGroupName: c.portalGroupName,
    sendAlertsToEmail: c.sendAlertsToEmail,
    ccAlertsToEmail: c.ccAlertsToEmail,
    ceOptOut: c.ceOptOut,
    ceGeneralNote: c.ceGeneralNote
  };
}

export function CoveredEntityGrid({
  currentUserId,
  currentUserName = 'User',
  currentUserAliases,
  onChanged,
  csvImport,
  searchPortalAlertGroups,
  stateStorageKey = 'covered-entity-grid'
}: CoveredEntityGridProps) {
  const { rows, loading, error, reload, applyUpsert, activeBarRoster, rowEditors, beginEditRow, endEditRow } =
    useLiveRows<CoveredEntityDto>(PatientTrackingBoardKeys.coveredEntities, patientTrackingApi.listCoveredEntities);

  const gridRef = useRef<DataGridRef>(null);
  const [pharmacies, setPharmacies] = useState<OurPharmacyDto[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [formViewMode, setFormViewMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CoveredEntityUpsertBody>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    patientTrackingApi.listPharmacies().then(setPharmacies).catch(() => setPharmacies([]));
  }, []);

  const pharmacyOptions = useMemo(
    () => pharmacies.map((p) => ({ id: p.id, name: p.pharmacyName })),
    [pharmacies]
  );

  const pharmacyLabel = useMemo(
    () => pharmacyOptions.find((p) => p.id === form.pharmacyId)?.name ?? '—',
    [form.pharmacyId, pharmacyOptions]
  );

  const openCreate = () => {
    setEditingId(null);
    setFormViewMode(false);
    setForm(emptyForm());
    setFormError(null);
    setEditorOpen(true);
  };

  const openView = (row: CoveredEntityDto) => {
    setEditingId(row.id);
    setFormViewMode(true);
    setForm(toBody(row));
    setFormError(null);
    setEditorOpen(true);
  };

  const enterEditMode = () => {
    if (!editingId) return;
    const lock = rowEditors.get(editingId);
    if (lock && lock.userId.toLowerCase() !== currentUserId.toLowerCase()) {
      window.alert(`${lock.displayName} is currently editing this covered entity.`);
      return;
    }
    setFormViewMode(false);
    beginEditRow(editingId);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    if (editingId && !formViewMode) endEditRow();
    setFormViewMode(false);
  };

  const save = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const body: CoveredEntityUpsertBody = {
        ...form,
        coveredEntityName: form.coveredEntityName.trim(),
        ceCodes: form.ceCodes?.trim() || null,
        portalGroupName: form.portalGroupName?.trim() || null,
        sendAlertsToEmail: form.sendAlertsToEmail?.trim() || null,
        ccAlertsToEmail: form.ccAlertsToEmail?.trim() || null,
        ceGeneralNote: form.ceGeneralNote?.trim() || null
      };
      if (!body.pharmacyId) throw new Error('Select a pharmacy.');
      const dto = editingId == null
        ? await patientTrackingApi.createCoveredEntity(body)
        : await patientTrackingApi.updateCoveredEntity(editingId, body);
      applyUpsert(dto);
      if (editingId) endEditRow();
      setEditorOpen(false);
      onChanged?.();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editingId) return;
    if (!window.confirm('Delete this covered entity?')) return;
    setSaving(true);
    try {
      await patientTrackingApi.deleteCoveredEntity(editingId);
      endEditRow();
      setEditorOpen(false);
      await reload();
      onChanged?.();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  const onRowPrepared = (e: RowPreparedEvent<CoveredEntityDto, string>) => {
    if (e.rowType !== 'data' || !e.data) return;
    e.rowElement.classList.remove('patient-ce-row--active', 'patient-ce-row--inactive');
    if (e.data.ceStatus === 'Active') e.rowElement.classList.add('patient-ce-row--active');
    else if (e.data.ceStatus === 'Inactive') e.rowElement.classList.add('patient-ce-row--inactive');
  };

  const csvImportUi = usePatientTrackingCsvImport({
    config: csvImport,
    disabled: loading,
    onImported: () => {
      void reload();
      onChanged?.();
    }
  });

  return (
    <>
      <DataGrid
            ref={gridRef}
            dataSource={rows}
            keyExpr="id"
            height="100%"
            width="100%"
            showBorders
            columnAutoWidth
            columnHidingEnabled
            allowColumnResizing
            allowColumnReordering
            columnResizingMode="widget"
            {...OPS_SITE_GRID_PROPS}
            noDataText={error ?? (loading ? 'Loading…' : 'No covered entities yet.')}
            onRowDblClick={(e) => {
              e.event?.stopPropagation();
              if (e.data) openView(e.data);
            }}
            onRowPrepared={onRowPrepared}
          >
            <OpsGridStateStoring
              storageKey={stateStorageKey}
              schemaVersion={COVERED_ENTITY_GRID_SCHEMA_VERSION}
              columns={COVERED_ENTITY_GRID_COLUMNS}
            />
            <Toolbar>
              <Item location="before">
                <ActiveUsersBar
                  roster={activeBarRoster}
                  currentUserId={currentUserId}
                  editingLabel="Editing:"
                />
              </Item>
              <PatientTrackingGridToolbarItems
                gridRef={gridRef}
                loading={loading}
                onRefresh={() => void reload()}
                newItem={{ hint: 'Add covered entity', onClick: openCreate }}
                uploadSlot={csvImportUi.uploadToolbarItem}
                uploadMenuItem={csvImportUi.uploadMenuItem}
                export={{
                  fileName: 'covered-entities',
                  worksheetName: 'Covered Entities',
                  hint: 'Export covered entities'
                }}
              />
              <Item name="searchPanel" location="after" />
            </Toolbar>
            <SearchPanel visible width={240} placeholder="Search covered entities…" />
            <HeaderFilter visible />
            <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
            <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
            <Paging defaultPageSize={50} />

            <Column dataField="ceStatus" caption="CE Status" width={120}>
              <Lookup dataSource={CE_STATUS_OPTIONS} valueExpr="value" displayExpr="label" />
            </Column>
            <Column dataField="pharmacyName" caption="Pharmacy" minWidth={160} />
            <Column dataField="coveredEntityName" caption="Covered Entity" minWidth={180} />
            <Column dataField="ceCodes" caption="CE Codes" minWidth={120} />
            <Column dataField="portalGroupName" caption="Portal Alert Group" minWidth={160} />
            <Column dataField="sendAlertsToEmail" caption="Send Alerts To" minWidth={180} />
            <Column dataField="ccAlertsToEmail" caption="CC Alerts To" minWidth={160} visible={false} />
            <Column dataField="ceOptOut" caption="Opted Out" dataType="boolean" width={100} />
            <Column dataField="totalRecords" caption="Total" dataType="number" width={80} />
            <Column dataField="pendingCount" caption="Pending" dataType="number" width={90} />
            <Column dataField="reviewedCount" caption="Reviewed" dataType="number" width={100} />
            <Column dataField="sentCount" caption="Sent" dataType="number" width={80} />
            <Column dataField="followUpCount" caption="Follow Up" dataType="number" width={100} />
            <Column dataField="onHoldCount" caption="On Hold" dataType="number" width={90} visible={false} />
            <Column dataField="resolvedCount" caption="Resolved" dataType="number" width={100} visible={false} />
            <Column dataField="lostCount" caption="Lost" dataType="number" width={80} visible={false} />
            <Column dataField="totalAlertsSent" caption="Total Alerts" dataType="number" width={110} visible={false} />
            <Column dataField="lastAlertSent" caption="Last Alert" dataType="datetime" width={160} visible={false} />
          </DataGrid>

      <RecordEditDiscussionPopup
        visible={editorOpen}
        viewMode={formViewMode}
        onRequestEdit={enterEditMode}
        title={
          editingId == null
            ? 'Add covered entity'
            : formViewMode
              ? 'View covered entity'
              : 'Edit covered entity'
        }
        onClose={closeEditor}
        sizeStorageKey={opsGridFormPopupSizeKey(stateStorageKey)}
        error={formError}
        discussionTargetType="covered_entity"
        discussionRecordId={editingId}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        currentUserAliases={currentUserAliases}
        discussionRecordLabel={
          form.coveredEntityName.trim()
            ? `Covered Entity: ${form.coveredEntityName.trim()}`
            : 'Covered Entity'
        }
        footer={
          <div className="patient-tracking-popup-footer">
            <div>
              {editingId != null ? (
                <Button text="Delete" type="danger" disabled={saving} onClick={() => void remove()} />
              ) : null}
            </div>
            <div className="patient-tracking-popup-footer__actions">
              <Button text="Cancel" disabled={saving} onClick={closeEditor} />
              <Button text={saving ? 'Saving…' : 'Save'} type="default" disabled={saving} onClick={() => void save()} />
            </div>
          </div>
        }
      >
        {formViewMode ? (
          <CoveredEntityDetailView form={form} pharmacyLabel={pharmacyLabel} />
        ) : (
          <div className="patient-form-grid">
            <label>
              <span className="patient-form-label patient-form-label--required">Pharmacy</span>
              <SelectBox
                dataSource={pharmacyOptions}
                valueExpr="id"
                displayExpr="name"
                searchEnabled
                value={form.pharmacyId || null}
                placeholder="Select pharmacy…"
                onValueChanged={(e) => setForm((f) => (f.pharmacyId === ((e.value as string) ?? '') ? f : { ...f, pharmacyId: (e.value as string) ?? '' }))}
              />
            </label>
            <label>
              <span className="patient-form-label patient-form-label--required">CE status</span>
              <SelectBox
                dataSource={CE_STATUS_OPTIONS}
                valueExpr="value"
                displayExpr="label"
                value={form.ceStatus}
                onValueChanged={(e) => setForm((f) => (f.ceStatus === ((e.value as CeStatus) ?? 'Pending') ? f : { ...f, ceStatus: (e.value as CeStatus) ?? 'Pending' }))}
              />
            </label>
            <label>
              <span className="patient-form-label patient-form-label--required">Covered entity name</span>
              <TextBox
                value={form.coveredEntityName}
                onValueChanged={(e) => setForm((f) => (f.coveredEntityName === (e.value ?? '') ? f : { ...f, coveredEntityName: e.value ?? '' }))}
              />
            </label>
            <label>
              <span className="patient-form-label">CE codes</span>
              <TextBox
                value={form.ceCodes ?? ''}
                onValueChanged={(e) => setForm((f) => ((f.ceCodes ?? '') === (e.value ?? '') ? f : { ...f, ceCodes: e.value ?? '' }))}
              />
            </label>

            <div className="patient-form-divider" />
            <div className="patient-form-group-label">Client portal</div>
            <label className="patient-form-grid__full">
              <span className="patient-form-label">Portal alert group</span>
              <EntraGroupDisplayNameField
                value={form.portalGroupName}
                searchGroups={searchPortalAlertGroups}
                onValueChange={(portalGroupName) =>
                  setForm((f) => (f.portalGroupName === portalGroupName ? f : { ...f, portalGroupName }))
                }
              />
              <span className="patient-form-field-hint">
                Search the client tenant group; PharmCollab users match on display name.
              </span>
            </label>

            <div className="patient-form-divider" />
            <div className="patient-form-group-label">Alerting</div>
            <label className="patient-form-grid__full">
              <span className="patient-form-label">Send alerts to (emails)</span>
              <TextBox
                value={form.sendAlertsToEmail ?? ''}
                placeholder="comma or semicolon separated"
                onValueChanged={(e) => setForm((f) => ((f.sendAlertsToEmail ?? '') === (e.value ?? '') ? f : { ...f, sendAlertsToEmail: e.value ?? '' }))}
              />
            </label>
            <label className="patient-form-grid__full">
              <span className="patient-form-label">CC alerts to (emails)</span>
              <TextBox
                value={form.ccAlertsToEmail ?? ''}
                placeholder="comma or semicolon separated"
                onValueChanged={(e) => setForm((f) => ((f.ccAlertsToEmail ?? '') === (e.value ?? '') ? f : { ...f, ccAlertsToEmail: e.value ?? '' }))}
              />
            </label>
            <CheckBox
              text="Opted out of alerts"
              value={form.ceOptOut}
              onValueChanged={(e) => setForm((f) => (f.ceOptOut === !!e.value ? f : { ...f, ceOptOut: !!e.value }))}
            />

            <div className="patient-form-divider" />
            <label className="patient-form-grid__full">
              <span className="patient-form-label">General note</span>
              <TextArea
                value={form.ceGeneralNote ?? ''}
                autoResizeEnabled
                minHeight={72}
                onValueChanged={(e) => setForm((f) => ((f.ceGeneralNote ?? '') === (e.value ?? '') ? f : { ...f, ceGeneralNote: e.value ?? '' }))}
              />
            </label>
          </div>
        )}
      </RecordEditDiscussionPopup>

      {csvImportUi.importPopup}
    </>
  );
}
